import { resolveEntityItemActiveHighlightColor, resolveEntityItemColor, resolveEntityItemLinkedHighlightColor, } from './spaceRendererHelpers.js';
const DEFAULT_PANEL_OUTLINE_OPACITY = 0.95;
const OUTLINED_PANEL_FACE_COLOR = 'white';
export function materialSpecKey(spec) {
    switch (spec.materialKind) {
        case 'basic':
            return `basic:${spec.color}:${spec.opacity}:${spec.doubleSided}:${spec.transparent}:${spec.depthWrite}`;
        case 'standard':
            return `standard:${spec.color}:${spec.opacity.toFixed(4)}:${spec.doubleSided ? 'ds' : 'fs'}`;
        case 'connector-tube':
            return `connector-tube:${spec.color}:${spec.opacity.toFixed(4)}:${spec.depthTest ? 'dt' : 'nodt'}`;
        case 'vertex-color-standard':
            return `vertex-color-standard:${spec.opacity.toFixed(4)}`;
        case 'line-basic':
            return `line-basic:${spec.color}:${spec.opacity.toFixed(4)}:${spec.width}`;
    }
}
function clamp01(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.max(0, Math.min(1, value));
}
export function isOutlinedShape(shape) {
    return (shape?.shapeKind === 'panel' || shape?.shapeKind === 'sector-slice')
        && shape.appearanceMode === 'outlined';
}
/** Spheres with the border cue keep their own color; the highlight moves to their contour. */
export function usesBorderHighlightCue(itemParametricState, shape) {
    return shape?.shapeKind === 'sphere' && itemParametricState?.visualStyle?.highlightCue === 'border';
}
/** Faces that are not view-aligned by construction must be visible from both sides. */
export function resolveDoubleSided(args) {
    const { layout, shape } = args;
    return layout.layoutKind === 'sector-ring' || shape?.shapeKind === 'panel';
}
export function resolveItemMaterialSpec(args) {
    const { precedence, itemParametricState, shape, baseOpacity, defaultColor, doubleSided } = args;
    const itemColor = resolveEntityItemColor(itemParametricState, defaultColor);
    // Outlined panels keep a transparent face at every opacity, otherwise a fading panel
    // would be drawn with the solid dimmed material and read as filled.
    if (isOutlinedShape(shape)) {
        return {
            materialKind: 'standard',
            color: OUTLINED_PANEL_FACE_COLOR,
            opacity: 0,
            doubleSided: true,
        };
    }
    if (precedence.isExplicitlyHidden) {
        return { materialKind: 'standard', color: itemColor, opacity: 0, doubleSided };
    }
    // Annular bands are gradient-shaded and wound facing -Y, so they never take a flat material.
    if (shape?.shapeKind === 'annular-band') {
        return { materialKind: 'vertex-color-standard', opacity: clamp01(baseOpacity) };
    }
    const opacity = clamp01(precedence.resolvedOpacity);
    if (precedence.isExplicitlyDimmed) {
        return { materialKind: 'standard', color: itemColor, opacity, doubleSided };
    }
    if (usesBorderHighlightCue(itemParametricState, shape)) {
        return { materialKind: 'standard', color: itemColor, opacity, doubleSided };
    }
    const activeHighlightColor = resolveEntityItemActiveHighlightColor(itemParametricState);
    if (precedence.isActive && activeHighlightColor) {
        return { materialKind: 'standard', color: activeHighlightColor, opacity, doubleSided };
    }
    const linkedHighlightColor = resolveEntityItemLinkedHighlightColor(itemParametricState);
    if (precedence.isLinkedActive && linkedHighlightColor) {
        return { materialKind: 'standard', color: linkedHighlightColor, opacity, doubleSided };
    }
    return { materialKind: 'standard', color: itemColor, opacity, doubleSided };
}
export function resolveOutlineMaterialSpec(args) {
    const { precedence, itemParametricState, shape, defaultColor, dimNonActiveOpacity } = args;
    const itemColor = resolveEntityItemColor(itemParametricState, defaultColor);
    const border = shape?.shapeKind === 'panel' || shape?.shapeKind === 'sector-slice'
        ? shape.border
        : undefined;
    const outlineColor = typeof border?.color === 'string'
        && border.color.length > 0
        ? border.color
        : itemColor;
    const outlineOpacity = typeof border?.opacity === 'number'
        ? border.opacity
        : DEFAULT_PANEL_OUTLINE_OPACITY;
    const outlineWidth = typeof border?.width === 'number'
        ? border.width
        : 1;
    const activeHighlightColor = resolveEntityItemActiveHighlightColor(itemParametricState);
    const linkedHighlightColor = resolveEntityItemLinkedHighlightColor(itemParametricState);
    const color = precedence.isExplicitlyDimmed
        ? outlineColor
        : precedence.isActive && activeHighlightColor
            ? activeHighlightColor
            : precedence.isLinkedActive && linkedHighlightColor
                ? linkedHighlightColor
                : outlineColor;
    // A border frames a slot even when its face is dimmed or invisible.
    const isBorder = !isOutlinedShape(shape);
    const opacity = precedence.isExplicitlyHidden
        ? 0
        : precedence.isExplicitlyDimmed && !isBorder
            ? outlineOpacity * precedence.resolvedOpacity
            : precedence.isDimmedNonActive
                ? outlineOpacity * (dimNonActiveOpacity ?? 1)
                : outlineOpacity;
    return { materialKind: 'line-basic', color, opacity: clamp01(opacity), width: outlineWidth };
}
